/**
 * 画布右键菜单
 *
 * 注意：菜单项必须使用 react-aria-components/Menu 的 MenuItem / MenuSection，
 * 不可使用 ContextMenu.Item（DropdownItem）。二者来自不同 RAC 实例时，
 * MenuItem 会报 "cannot be rendered outside a collection"。
 * @see https://heroui.pro/docs/react/components/context-menu
 */

import type { ComponentType, Key, ReactNode } from 'react';
import { Kbd, Label, cn } from '@heroui/react';
import { ContextMenu } from '@heroui-pro/react';
import { MenuItem, MenuSection, Separator } from 'react-aria-components/Menu';
import {
  IconClipboard,
  IconClipboardPlus,
  IconCut,
  IconEye,
  IconEyeClosed,
  IconLayoutGridAdd,
  IconLayoutGridRemove,
  IconLock,
  IconLockOpen,
  IconSquareChevronDown,
  IconSquareChevronUp,
  IconSquareChevronsDown,
  IconSquareChevronsUp,
  IconTrash,
} from '@tabler/icons-react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { Element } from '@/components/canvas/document/publicTypes';
import type { WorkspaceInteractionApi } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';
import {
  resolveContextMenuActionIds,
  resolveGroupableRootIds,
} from '@/components/canvas/workspace/model/contextMenuSelection';

type MenuAction =
  | 'paste'
  | 'copy'
  | 'cut'
  | 'bring-front'
  | 'bring-forward'
  | 'send-backward'
  | 'send-back'
  | 'group'
  | 'ungroup'
  | 'toggle-lock'
  | 'toggle-visible'
  | 'delete';

type MenuIcon = ComponentType<{ className?: string; stroke?: number }>;

interface CanvasContextMenuProps {
  interaction: WorkspaceInteractionApi;
  children: ReactNode;
}

interface CanvasContextMenuItemProps {
  id: MenuAction;
  label: string;
  icon: MenuIcon;
  kbd?: string[];
  isDisabled?: boolean;
  tone?: 'default' | 'danger';
}

function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

function findElementById(elements: Element[], id: string): Element | null {
  return elements.find((el) => el.id === id) ?? null;
}

function renderCanvasMenuItem({
  id,
  label,
  icon: Icon,
  kbd,
  isDisabled = false,
  tone = 'default',
}: CanvasContextMenuItemProps) {
  return (
    <MenuItem
      id={id}
      textValue={label}
      isDisabled={isDisabled}
      className={cn(
        'group flex min-w-48 cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none',
        'data-[focused]:bg-default-100 data-[hovered]:bg-default-100',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        tone === 'danger' && 'text-danger data-[focused]:text-danger data-[hovered]:text-danger',
      )}
    >
      <Icon className="size-5 shrink-0" stroke={1.8} />
      <Label className="font-normal">{label}</Label>
      {kbd ? (
        <span className="ml-auto flex shrink-0 items-center gap-0.5 text-muted-foreground">
          {kbd.map((key) => (
            <Kbd key={key}>{key}</Kbd>
          ))}
        </span>
      ) : null}
    </MenuItem>
  );
}

export function CanvasContextMenu({ interaction, children }: CanvasContextMenuProps) {
  const {
    getCurrentPage,
    selectedElementIds,
    selectionScope,
    hasClipboard,
    copySelectedElements,
    cutSelectedElements,
    pasteFromSystemClipboardAt,
    bringToFront,
    bringForward,
    sendBackward,
    sendToBack,
    groupElements,
    ungroupElement,
    pushHistory,
    updateElementSilentInDocument,
    deleteSelectedElements,
  } = useCanvas();

  const target = interaction.contextMenuTarget;
  const isMac = isMacPlatform();
  const mod = isMac ? '⌘' : 'Ctrl';

  if (interaction.isReadonlyMode) {
    return <>{children}</>;
  }

  const pageElements = getCurrentPage()?.elements ?? [];
  const targetElement = target?.type === 'element' ? target.element : null;
  const actionElementIds = resolveContextMenuActionIds(
    selectedElementIds,
    targetElement,
    pageElements,
  );
  const actionElements = actionElementIds
    .map((id) => findElementById(pageElements, id) ?? findElementById(interaction.workspaceElements, id))
    .filter(Boolean) as Element[];
  const pageActionElements = actionElements.filter((el) => pageElements.some((candidate) => candidate.id === el.id));
  const allActionElementsArePageElements =
    actionElements.length > 0 && actionElements.length === pageActionElements.length;
  const arrangeRootIds = pageActionElements
    .filter((el) => !el.layout.parentId)
    .map((el) => el.id);
  const groupableRootIds = resolveGroupableRootIds(pageElements, actionElementIds);
  const canArrange = allActionElementsArePageElements && arrangeRootIds.length > 0;
  const canGroup = allActionElementsArePageElements && groupableRootIds.length >= 2;
  const ungroupTarget =
    selectedElementIds.length === 1
    && actionElementIds.length === 1
    && pageActionElements.length === 1
    && pageActionElements[0].type === 'group'
      ? pageActionElements[0]
      : null;
  const canCopyOrCut =
    allActionElementsArePageElements && actionElementIds.length > 0 && selectionScope !== 'workspace';
  const canPaste = !!hasClipboard;
  const canMutateElement = actionElements.length > 0;
  const shouldUnlock = canMutateElement && actionElements.every((el) => el.interaction.locked);
  const shouldShow = canMutateElement && actionElements.every((el) => el.interaction.visible === false);

  const applyInteractionPatch = (patch: Partial<Element['interaction']>) => {
    if (!canMutateElement) return;
    pushHistory();
    actionElements.forEach((el) => updateElementSilentInDocument(el.id, { interaction: patch }));
  };

  const handleAction = (key: Key) => {
    const action = String(key) as MenuAction;
    if (action === 'paste') {
      if (canPaste && target?.type === 'page') {
        void pasteFromSystemClipboardAt({
          scope: 'page',
          pageId: target.pageId,
          x: target.point.x,
          y: target.point.y,
        });
      } else if (canPaste && target?.type === 'workspace') {
        void pasteFromSystemClipboardAt({
          scope: 'workspace',
          x: target.point.x,
          y: target.point.y,
        });
      }
      return;
    }
    if (action === 'copy') {
      if (canCopyOrCut) copySelectedElements();
      return;
    }
    if (action === 'cut') {
      if (canCopyOrCut) cutSelectedElements();
      return;
    }
    if (action === 'bring-front') {
      if (canArrange) bringToFront(arrangeRootIds);
      return;
    }
    if (action === 'bring-forward') {
      if (canArrange) bringForward(arrangeRootIds);
      return;
    }
    if (action === 'send-backward') {
      if (canArrange) sendBackward(arrangeRootIds);
      return;
    }
    if (action === 'send-back') {
      if (canArrange) sendToBack(arrangeRootIds);
      return;
    }
    if (action === 'group') {
      if (canGroup) groupElements(groupableRootIds);
      return;
    }
    if (action === 'ungroup') {
      if (ungroupTarget) ungroupElement(ungroupTarget.id);
      return;
    }
    if (action === 'toggle-lock') {
      applyInteractionPatch({ locked: !shouldUnlock });
      return;
    }
    if (action === 'toggle-visible') {
      applyInteractionPatch({ visible: !shouldShow });
      return;
    }
    if (action === 'delete') {
      if (canMutateElement) deleteSelectedElements();
    }
  };

  const renderMenuContent = () => {
    if (!target) return null;

    if (target.type === 'workspace' || target.type === 'page') {
      return (
        <ContextMenu.Menu aria-label="画布右键菜单" onAction={handleAction}>
          <MenuSection>
            {renderCanvasMenuItem({
              id: 'paste',
              label: '粘贴',
              icon: IconClipboardPlus,
              kbd: [mod, 'V'],
              isDisabled: !canPaste,
            })}
          </MenuSection>
        </ContextMenu.Menu>
      );
    }

    return (
      <ContextMenu.Menu aria-label="画布右键菜单" onAction={handleAction}>
        <MenuSection>
          {renderCanvasMenuItem({
            id: 'copy',
            label: '复制',
            icon: IconClipboard,
            kbd: [mod, 'C'],
            isDisabled: !canCopyOrCut,
          })}
          {renderCanvasMenuItem({
            id: 'cut',
            label: '剪切',
            icon: IconCut,
            kbd: [mod, 'X'],
            isDisabled: !canCopyOrCut,
          })}
        </MenuSection>

        <Separator className="mx-1 my-0.5 h-px bg-separator" />

        <MenuSection>
          {renderCanvasMenuItem({
            id: 'bring-front',
            label: '置于顶层',
            icon: IconSquareChevronsUp,
            kbd: [mod, 'T'],
            isDisabled: !canArrange,
          })}
          {renderCanvasMenuItem({
            id: 'bring-forward',
            label: '上移一层',
            icon: IconSquareChevronUp,
            kbd: [mod, '↑'],
            isDisabled: !canArrange,
          })}
          {renderCanvasMenuItem({
            id: 'send-backward',
            label: '下移一层',
            icon: IconSquareChevronDown,
            kbd: [mod, '↓'],
            isDisabled: !canArrange,
          })}
          {renderCanvasMenuItem({
            id: 'send-back',
            label: '置于底层',
            icon: IconSquareChevronsDown,
            kbd: [mod, 'B'],
            isDisabled: !canArrange,
          })}
        </MenuSection>

        <Separator className="mx-1 my-0.5 h-px bg-separator" />

        <MenuSection>
          {ungroupTarget
            ? renderCanvasMenuItem({
                id: 'ungroup',
                label: '取消编组',
                icon: IconLayoutGridRemove,
                kbd: [mod, 'Shift', 'G'],
              })
            : renderCanvasMenuItem({
                id: 'group',
                label: '编组',
                icon: IconLayoutGridAdd,
                kbd: [mod, 'G'],
                isDisabled: !canGroup,
              })}
          {renderCanvasMenuItem({
            id: 'toggle-lock',
            label: shouldUnlock ? '解锁' : '锁定',
            icon: shouldUnlock ? IconLockOpen : IconLock,
            kbd: shouldUnlock ? [mod, 'Shift', 'L'] : [mod, 'L'],
          })}
          {renderCanvasMenuItem({
            id: 'toggle-visible',
            label: shouldShow ? '显示' : '隐藏',
            icon: shouldShow ? IconEye : IconEyeClosed,
            kbd: shouldShow ? [mod, 'Shift', 'H'] : [mod, 'H'],
          })}
        </MenuSection>

        <Separator className="mx-1 my-0.5 h-px bg-separator" />

        <MenuSection>
          {renderCanvasMenuItem({
            id: 'delete',
            label: '删除',
            icon: IconTrash,
            kbd: [isMac ? '⌫' : 'Del'],
            tone: 'danger',
          })}
        </MenuSection>
      </ContextMenu.Menu>
    );
  };

  return (
    <ContextMenu
      onOpenChange={(open) => {
        if (!open) interaction.closeContextMenu();
      }}
    >
      <ContextMenu.Trigger className="contents">{children}</ContextMenu.Trigger>
      <ContextMenu.Popover offset={4} placement="bottom start" className="z-[260] min-w-48 max-w-64">
        {renderMenuContent()}
      </ContextMenu.Popover>
    </ContextMenu>
  );
}
